import React, { useEffect, useRef, useState } from 'react';

// Онлайн-камера ветеринарного кабинета (камера 5) через go2rtc (MSE, низкая задержка),
// проксируется nginx по пути /vetcam/.
// - на вертикальном экране (телефон) видео автоматически поворачивается на бок и растягивается;
// - зум щипком двумя пальцами (1x–4x), перетаскивание одним пальцем, двойной тап — сброс,
//   одиночный тап — вкл/выкл звук.
function CameraView() {
  const wrapRef = useRef(null);
  const iframeRef = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState({ scale: 1, tx: 0, ty: 0 });

  // измеряем область (для автоповорота и ограничения панорамы)
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('orientationchange', update);
    return () => { ro.disconnect(); window.removeEventListener('orientationchange', update); };
  }, []);

  // ограничение сдвига, чтобы картинка не уезжала за край
  const clamp = (scale, tx, ty) => {
    const maxX = (scale - 1) * box.w / 2;
    const maxY = (scale - 1) * box.h / 2;
    return {
      scale,
      tx: Math.max(-maxX, Math.min(maxX, tx)),
      ty: Math.max(-maxY, Math.min(maxY, ty)),
    };
  };

  // звук: достаём <video> внутри iframe (same-origin) и переключаем
  const toggleSound = () => {
    try {
      const v = iframeRef.current?.contentDocument?.querySelector('video');
      if (v) {
        v.muted = !v.muted;
        if (!v.muted) v.play().catch(() => {});
      }
    } catch (e) { /* ignore */ }
  };

  // обработка жестов
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const g = {
      mode: null,        // 'pinch' | 'pan'
      startDist: 0,
      startScale: 1,
      startTx: 0, startTy: 0,
      startX: 0, startY: 0,
      moved: false,
      lastTap: 0,
    };
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    const onStart = (e) => {
      if (e.touches.length === 2) {
        g.mode = 'pinch';
        g.startDist = dist(e.touches);
        setZoom((z) => { g.startScale = z.scale; g.startTx = z.tx; g.startTy = z.ty; return z; });
        g.moved = true;
        e.preventDefault();
      } else if (e.touches.length === 1) {
        g.mode = 'pan';
        g.startX = e.touches[0].clientX;
        g.startY = e.touches[0].clientY;
        setZoom((z) => { g.startScale = z.scale; g.startTx = z.tx; g.startTy = z.ty; return z; });
        g.moved = false;
      }
    };

    const onMove = (e) => {
      if (g.mode === 'pinch' && e.touches.length === 2) {
        const s = Math.max(1, Math.min(4, g.startScale * (dist(e.touches) / g.startDist)));
        setZoom(clamp(s, g.startTx, g.startTy));
        e.preventDefault();
      } else if (g.mode === 'pan' && e.touches.length === 1) {
        const dx = e.touches[0].clientX - g.startX;
        const dy = e.touches[0].clientY - g.startY;
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) g.moved = true;
        if (g.startScale > 1) {
          setZoom(clamp(g.startScale, g.startTx + dx, g.startTy + dy));
          e.preventDefault();
        }
      }
    };

    const onEnd = (e) => {
      // завершение всех касаний
      if (e.touches.length === 0) {
        const now = Date.now();
        if (g.mode === 'pan' && !g.moved) {
          // одиночный тап
          if (now - g.lastTap < 300) {
            // двойной тап — сброс зума
            setZoom({ scale: 1, tx: 0, ty: 0 });
            g.lastTap = 0;
          } else {
            g.lastTap = now;
            toggleSound();
          }
        }
        g.mode = null;
      }
    };

    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
    };
  }, [box.w, box.h]);

  const portrait = box.h > box.w;

  const iframeStyle = portrait
    ? {
        position: 'absolute', top: '50%', left: '50%',
        width: box.h + 'px', height: box.w + 'px',
        transform: 'translate(-50%, -50%) rotate(90deg)',
        transformOrigin: 'center center',
        border: 'none', background: '#000', pointerEvents: 'none',
      }
    : {
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        border: 'none', background: '#000', pointerEvents: 'none',
      };

  return (
    <div
      ref={wrapRef}
      style={{ flex: 1, position: 'relative', overflow: 'hidden', background: '#000', minHeight: 0, touchAction: 'none' }}
    >
      {/* зум-слой */}
      <div
        style={{
          position: 'absolute', inset: 0,
          transform: `translate(${zoom.tx}px, ${zoom.ty}px) scale(${zoom.scale})`,
          transformOrigin: 'center center',
          transition: zoom.scale === 1 ? 'transform 0.15s ease-out' : 'none',
        }}
      >
        <iframe
          ref={iframeRef}
          title="Веткабинет — онлайн-камера"
          src="/vetcam/stream.html?src=vetcam&mode=mse&media=video+audio"
          allow="autoplay; fullscreen"
          style={iframeStyle}
        />
      </div>
    </div>
  );
}

export default CameraView;
